import { formatDistanceToNow } from "date-fns";
import { Mail, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const truncateToWords = (text: string, maxWords: number = 8): string => {
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
};

export interface EmailThread {
  id: string;
  sender_name: string | null;
  sender_email: string | null;
  subject: string;
  status: string;
  unread_count: number;
  last_message_at: string;
  is_archived: boolean;
  inquiry_id: string | null;
  latest_email_preview?: string;
  inquiry?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    wedding_date: string | null;
    venue: string | null;
    services: string[] | null;
    budget: string | null;
    message: string | null;
    selected_package: string | null;
  } | null;
}

interface EmailThreadListProps {
  threads: EmailThread[];
  selectedThreadId: string | null;
  onSelectThread: (thread: EmailThread) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  loading: boolean;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  open: { label: "Open", variant: "default" },
  awaiting_reply: { label: "Awaiting Reply", variant: "secondary" },
  closed: { label: "Closed", variant: "outline" },
};

export function EmailThreadList({
  threads,
  selectedThreadId,
  onSelectThread,
  statusFilter,
  onStatusFilterChange,
  searchQuery,
  onSearchChange,
  loading,
}: EmailThreadListProps) {
  return (
    <div className="flex flex-col h-full border-r border-border bg-card">
      {/* Search and Filter Header */}
      <div className="p-4 space-y-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search emails..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Threads</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="awaiting_reply">Awaiting Reply</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Thread List */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            Loading threads...
          </div>
        ) : threads.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No email threads found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {threads.map((thread) => (
                <button
                key={thread.id}
                onClick={() => onSelectThread(thread)}
                className={cn(
                  "w-full text-left p-4 hover:bg-accent/50 transition-colors",
                  selectedThreadId === thread.id && "ring-2 ring-primary ring-inset",
                  thread.unread_count > 0 && selectedThreadId !== thread.id && "bg-primary/5"
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className={cn(
                    "font-medium truncate flex-1",
                    thread.unread_count > 0 && "font-semibold"
                  )}>
                    {thread.inquiry 
                      ? `${thread.inquiry.first_name} ${thread.inquiry.last_name}` 
                      : (thread.sender_name || thread.sender_email || "Unknown")}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn(
                    "text-sm truncate flex-1",
                    thread.unread_count > 0 ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {thread.subject}
                  </span>
                  {thread.unread_count > 0 && (
                    <Badge variant="default" className="text-xs px-1.5 py-0">
                      {thread.unread_count}
                    </Badge>
                  )}
                </div>
                
                {thread.latest_email_preview && (
                  <p className="text-xs text-muted-foreground">
                    {truncateToWords(thread.latest_email_preview, 8)}
                  </p>
                )}
                
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={statusConfig[thread.status]?.variant || "outline"} className="text-xs">
                    {statusConfig[thread.status]?.label || thread.status}
                  </Badge>
                  {thread.inquiry_id && (
                    <Badge variant="outline" className="text-xs">
                      Linked
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
